import { ArrowRight, Shield, CheckCircle, Globe, Banknote, FileText, Video } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useState } from 'react';
import SEO from '../SEO';
import { pageSEO, faqSchemas } from '../../seoData';
import RelatedServices from '../RelatedServices';

const NriUK = () => {
  const [expandedFaq, setExpandedFaq] = useState(null);

  const faqs = [
    { question: "Can UK-based NRIs invest in Indian mutual funds?", answer: "Yes! UK NRIs can invest in Indian mutual funds through NRE/NRO accounts. Most fund houses accept NRI investments. We handle the complete KYC, documentation, and fund selection process. Investments through NRE accounts are fully repatriable to the UK." },
    { question: "How does the India-UK DTAA benefit UK NRIs?", answer: "The Double Taxation Avoidance Agreement between India and UK prevents you from paying tax on the same income in both countries. Capital gains tax paid in India can be claimed as credit against UK tax liability. We help you optimize investments to maximize DTAA benefits." },
    { question: "Can I transfer my UK pension to Indian investments?", answer: "While direct pension transfer isn't possible, you can invest your UK earnings in India through NRE accounts. Returns and principal are fully repatriable. We help UK NRIs create a parallel investment portfolio in India that complements their UK pension and ISA holdings." },
    { question: "How do I manage Indian investments from the UK?", answer: "We offer complete remote management via video calls, WhatsApp, and email. Portfolio reviews are conducted quarterly via Zoom. You can execute transactions online 24/7. We work across time zones - evening appointments available to suit UK working hours (IST is +5:30 ahead of GMT)." },
  ];

  const services = [
    { icon: <Banknote className="h-7 w-7 text-white" />, title: "GBP to INR Investments", desc: "Strategic investment of your UK earnings in Indian markets. We help you time currency conversion and invest in assets that benefit from GBP/INR movements for optimal returns." },
    { icon: <Shield className="h-7 w-7 text-white" />, title: "DTAA Tax Optimization", desc: "Maximize India-UK DTAA benefits. Claim tax credits in the UK for Indian capital gains tax paid. We structure your portfolio for optimal tax treatment in both countries." },
    { icon: <FileText className="h-7 w-7 text-white" />, title: "NRE/NRO Account Setup", desc: "Complete assistance with opening and managing NRE (fully repatriable) and NRO accounts. We guide you through documentation requirements for UK-based NRIs." },
    { icon: <Globe className="h-7 w-7 text-white" />, title: "Pension Complementing", desc: "Build an Indian investment portfolio that complements your UK pension and ISA holdings. Diversify geographically with India's high-growth market opportunities." },
    { icon: <Video className="h-7 w-7 text-white" />, title: "UK Time Zone Support", desc: "Video consultations scheduled to suit UK working hours. Evening appointments available (IST +5:30 GMT). WhatsApp and email support for quick queries anytime." },
    { icon: <CheckCircle className="h-7 w-7 text-white" />, title: "Full Repatriation Support", desc: "NRE account investments are 100% repatriable. We handle all RBI and FEMA documentation for seamless transfer of returns and principal back to your UK bank account." },
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEO {...pageSEO.nriUK} faqJsonLd={faqSchemas.nriUK} />

      <section className="relative bg-gradient-to-br from-blue-900 via-blue-800 to-indigo-900 pt-20 pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl">
            <span className="inline-block bg-red-500 text-white px-4 py-2 rounded-full text-sm font-semibold tracking-wide mb-6">
              UK NRI INVESTMENT SERVICES
            </span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
              India Investments for
              <span className="block bg-gradient-to-r from-red-400 to-blue-400 bg-clip-text text-transparent">
                UK-Based NRIs
              </span>
            </h1>
            <p className="text-lg text-blue-200 mb-8 leading-relaxed max-w-3xl">
              Invest in India's high-growth markets from the UK with expert guidance from Rumtek Finvest. 
              We've been helping UK NRIs since 2009 with DTAA-optimized investment strategies, NRE/NRO 
              account management, and complete repatriation support. Benefit from India-UK tax treaty 
              provisions while building wealth in one of the world's fastest-growing economies.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/contact" className="bg-white text-blue-900 px-8 py-4 rounded-xl font-bold text-lg hover:bg-gray-100 transition-all transform hover:scale-105 inline-flex items-center justify-center shadow-xl" data-testid="nri-uk-cta">
                Free NRI Consultation <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
              <Link to="/nri-financial-planning" className="border-2 border-white text-white hover:bg-white hover:text-blue-900 px-8 py-4 rounded-xl font-bold text-lg transition-all inline-flex items-center justify-center">
                General NRI Services
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section className="py-12 bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 text-center">
            <div><div className="text-3xl font-bold text-blue-900 mb-1">Since 2009</div><div className="text-gray-600 text-sm">Serving UK NRIs</div></div>
            <div><div className="text-3xl font-bold text-blue-900 mb-1">DTAA</div><div className="text-gray-600 text-sm">India-UK Tax Treaty</div></div>
            <div><div className="text-3xl font-bold text-blue-900 mb-1">100%</div><div className="text-gray-600 text-sm">Repatriable via NRE</div></div>
            <div><div className="text-3xl font-bold text-blue-900 mb-1">UK Hours</div><div className="text-gray-600 text-sm">Evening Consultations</div></div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4 text-center">Tailored Services for UK NRIs</h2>
          <p className="text-lg text-gray-600 text-center mb-12 max-w-2xl mx-auto">Investment strategies designed specifically for NRIs living and working in the United Kingdom</p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((item, i) => (
              <div key={i} className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all">
                <div className="w-14 h-14 bg-gradient-to-br from-blue-600 to-indigo-700 rounded-2xl flex items-center justify-center mb-5">{item.icon}</div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{item.title}</h3>
                <p className="text-gray-600 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4 text-center">India-UK DTAA Benefits</h2>
          <p className="text-lg text-gray-600 text-center mb-10">Key tax treaty provisions that benefit UK NRI investors</p>
          <div className="space-y-4">
            {[
              { title: "Capital Gains Tax Credit", desc: "Tax paid on capital gains in India (10% LTCG, 15% STCG) can be claimed as credit against your UK tax liability, effectively avoiding double taxation." },
              { title: "Dividend Income", desc: "Dividends from Indian companies are taxed in India. Under DTAA, the tax rate may be reduced, and credit can be claimed in the UK." },
              { title: "Interest Income", desc: "Interest from NRO accounts and bonds is subject to TDS in India (typically 30%). DTAA reduces this to 15% and allows UK tax credit for the remainder." },
              { title: "Property Income", desc: "Rental income from Indian property is taxable in India. Under DTAA, you can claim credit for Indian tax paid when filing UK self-assessment." },
            ].map((item, i) => (
              <div key={i} className="bg-blue-50 rounded-xl p-6 border border-blue-100">
                <h3 className="font-bold text-blue-900 text-lg mb-2">{item.title}</h3>
                <p className="text-gray-700">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-10 text-center">UK NRI Investment FAQs</h2>
          <div className="space-y-4">
            {faqs.map((faq, i) => (
              <div key={i} className="bg-white rounded-xl shadow-sm overflow-hidden">
                <button onClick={() => setExpandedFaq(expandedFaq === i ? null : i)} className="w-full px-6 py-5 text-left flex justify-between items-center">
                  <span className="font-semibold text-gray-900">{faq.question}</span>
                  <span className="text-2xl text-blue-600 ml-4">{expandedFaq === i ? '−' : '+'}</span>
                </button>
                {expandedFaq === i && <div className="px-6 pb-5"><p className="text-gray-700 leading-relaxed">{faq.answer}</p></div>}
              </div>
            ))}
          </div>
        </div>
      </section>

      <RelatedServices links={['nriUAE', 'nriUS', 'mutualFund', 'pms', 'retirement']} />

      <section className="py-16 bg-gradient-to-r from-blue-900 to-indigo-900">
        <div className="max-w-4xl mx-auto px-4 text-center text-white">
          <h2 className="text-3xl font-bold mb-4">Start Investing in India from the UK</h2>
          <p className="text-lg mb-8 text-blue-200">Book a free video consultation at a time that suits your UK schedule.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact" className="bg-white text-blue-900 px-8 py-4 rounded-xl font-bold text-lg hover:bg-gray-100 transition-all inline-flex items-center">
              Book Consultation <ArrowRight className="ml-2 h-5 w-5" />
            </Link>
            <a href="mailto:rumtekmf@gmail.com" className="border-2 border-white text-white px-8 py-4 rounded-xl font-bold text-lg hover:bg-white hover:text-blue-900 transition-all inline-flex items-center">
              Email Us
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default NriUK;
